import { useContentfulLiveUpdates } from "@contentful/live-preview/react"
import React, { useState } from "react"
import { parseISO, compareAsc } from "date-fns"
import dynamic from "next/dynamic"

// ---------------------------------------------------------

import { getAllWebinarResourcesData } from "@lib/api"
import { fetchRecommended } from "@lib/fetchRecommended"
import { getCurrentDate } from "@lib/utils"
import FlexibleData from "@lib/flexibleData"
import RenderBlocks from "@lib/renderBlocks"

// ---------------------------------------------------------

import Button from "@components/button"
import Card from "@components/card"
import Layout from "../../../layout"
import NewsletterSignUp from "@components/forms/newsletter-signup"
import PingPong from "@components/pingpong"

const RecommendedReading = dynamic(() => import("@components/recommended-reading"), { ssr: false })

// ---------------------------------------------------------

import {
  button_container,
  cards_container_thirds,
  cards_container_halves,
  header
} from "./styles.module.scss"

// ---------------------------------------------------------

const LIMIT = 12

// ---------------------------------------------------------

export async function getStaticProps(context) {
  const preview = process.env.NODE_ENV === "development" || context.draftMode || false
  const previewEnv = context?.previewData?.environment || null
  const pageData = (await FlexibleData.fetch("webinars", preview, previewEnv)) ?? []
  const resourcesData = (await getAllWebinarResourcesData(preview)) ?? []

  const recommended = pageData.showRecommended
    ? await fetchRecommended(pageData?.recommendedCollection?.items, "webinars", "blogs", preview)
    : null

  return {
    props: {
      pageData,
      resourcesData,
      recommended,
      preview
    }
  }
}

const WebinarsIndexPage = (props) => {
  const { pageData, resourcesData, recommended, preview } = props
  const livePageData = useContentfulLiveUpdates(pageData)

  const upcomingWebinarData = resourcesData.filter(
    (resource) => compareAsc(getCurrentDate(), parseISO(resource.startDate)) < 0
  )
  const onDemandWebinarData = resourcesData.filter(
    (resource) => compareAsc(getCurrentDate(), parseISO(resource.startDate)) >= 0
  )

  const [endWebinarPost, setEndWebinarPost] = useState(LIMIT)
  const filteredWebinarPosts = onDemandWebinarData.slice(0, endWebinarPost)

  const pageModules = livePageData?.modulesCollection?.items ?? []
  let pageTitle = "Webinars"
  let excerpt =
    "This is your all-in-one-place hub for upcoming and on-demand webinars — explore expert perspectives, trends, and insights."

  const threeOrMoreWebinars = upcomingWebinarData.length >= 3

  return (
    <Layout preview={preview} pageTitle={pageTitle} excerpt={excerpt}>
      {pageModules && RenderBlocks(pageModules)}

      {upcomingWebinarData.length > 0 && <h2 className={header}>Upcoming webinars</h2>}

      {/* Upcoming webinars */}
      {upcomingWebinarData.length > 0 && (
        <div className={threeOrMoreWebinars ? cards_container_thirds : cards_container_halves}>
          {upcomingWebinarData.map((webinar, index) => {
            return (
              <div key={index}>
                <Card
                  theme="hover-grid"
                  title={webinar.title}
                  label="Webinar"
                  image={{
                    alt: webinar.featuredImage.description,
                    src: webinar.featuredImage.url,
                    height: webinar.featuredImage.height,
                    width: webinar.featuredImage.width
                  }}
                  startDate={webinar.startDate}
                  endDate={webinar.endDate}
                  description={webinar.excerpt}
                  buttonUrl={`/resources/webinars/${encodeURIComponent(webinar.slug)}`}
                />
              </div>
            )
          })}
        </div>
      )}
      {endWebinarPost < upcomingWebinarData.length - 1 ? (
        <>
          {endWebinarPost < upcomingWebinarData.length - 1 && (
            <div className={button_container}>
              <Button theme="dark" onClick={() => setEndWebinarPost(endWebinarPost + LIMIT)}>
                Show more
              </Button>
            </div>
          )}
        </>
      ) : null}

      {/* On-demand webinars */}
      <h2 className={header}>On-demand webinars</h2>

      <div className={cards_container_thirds}>
        {filteredWebinarPosts.map((card, index) => {
          return (
            <div key={index}>
              <Card
                buttonUrl={`/resources/webinars/${encodeURIComponent(card.slug)}`}
                description={card.excerpt}
                label="Webinar"
                image={{
                  alt: card.featuredImage?.description,
                  height: 700,
                  src: card.featuredImage?.url,
                  width: 1080
                }}
                theme="hover-grid"
                title={card.title}
                startDate={card.startDate}
              />
            </div>
          )
        })}
      </div>
      {endWebinarPost < onDemandWebinarData.length - 1 && (
        <div className={button_container}>
          <Button theme="dark" onClick={() => setEndWebinarPost(endWebinarPost + LIMIT)}>
            Show more
          </Button>
        </div>
      )}

      <PingPong
        imageOnLeft
        backgroundColor="off-black"
        image={{
          src: "https://amperity.imgix.net/1M1CJN4NxglEj8glQDY7f0/ab3367dcb9d30751e72b09962369ee8d/Ampersend_Hero.jpg?auto=format,compress&w=1620&h=1013",
          alt: "Hand holding mobile device displaying Ampersend",
          width: 1600,
          height: 1080
        }}
      >
        <span className="label">Newsletter</span>
        <h2>Be the first to know about upcoming Amperity webinars & events</h2>
        <p>
          Submit your email below to join thousands of marketing, analytics, and data professionals
          who receive our monthly newsletter.
        </p>
        <NewsletterSignUp theme="gray-background" formId="2028" />
      </PingPong>

      {recommended && (
        <RecommendedReading
          recommended={recommended}
          title="Explore Blogs"
          buttonLabel="View all blogs"
          buttonUrl="/blog"
        ></RecommendedReading>
      )}
    </Layout>
  )
}

export default WebinarsIndexPage
